
import { useState, useCallback } from "react";
import { WIDGET_REGISTRY, DEFAULT_WIDGETS, type WidgetDef } from "@/lib/widget-registry";

interface Layout {
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  minW?: number;
  minH?: number;
}

const STORAGE_KEY = "cg-dashboard-layout-v5";
const CACHE_TTL_KEY = "cg-cache-ttl";

interface DashboardState {
  widgetIds: string[];
  layouts: Layout[];
}

const COLS_LG = 4;

/** Scan grid top-left → bottom-right and return the first slot of size w×h that fits. */
function findFreeSlot(layouts: Layout[], w: number, h: number, cols = COLS_LG): { x: number; y: number } {
  const maxY = layouts.reduce((m, l) => Math.max(m, l.y + l.h), 0);
  const rows = maxY + h + 1;
  const grid: boolean[][] = Array.from({ length: rows }, () => Array(cols).fill(false));

  for (const l of layouts) {
    for (let yy = l.y; yy < l.y + l.h && yy < rows; yy++) {
      for (let xx = l.x; xx < Math.min(l.x + l.w, cols); xx++) {
        grid[yy][xx] = true;
      }
    }
  }

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x + w <= cols; x++) {
      let fits = true;
      for (let yy = y; yy < y + h && fits; yy++) {
        for (let xx = x; xx < x + w && fits; xx++) {
          if (grid[yy]?.[xx]) fits = false;
        }
      }
      if (fits) return { x, y };
    }
  }
  return { x: 0, y: maxY };
}

function generateDefaultLayout(widgetIds: string[]): Layout[] {
  const layouts: Layout[] = [];
  widgetIds.forEach((id) => {
    const def = WIDGET_REGISTRY.find((w) => w.id === id);
    if (!def) return;
    const { x, y } = findFreeSlot(layouts, def.defaultW, def.defaultH);
    layouts.push({
      i: id,
      x,
      y,
      w: def.defaultW,
      h: def.defaultH,
      minW: def.minW,
      minH: def.minH,
    });
  });
  return layouts;
}

function loadInitialState(): DashboardState {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as DashboardState;
      // Validate widget IDs exist in registry
      const validIds = parsed.widgetIds.filter((id) =>
        WIDGET_REGISTRY.some((w) => w.id === id)
      );
      if (validIds.length > 0) {
        return {
          widgetIds: validIds,
          layouts: parsed.layouts.filter((l) => validIds.includes(l.i)),
        };
      }
    }
  } catch {
    // ignore parse errors
  }
  return {
    widgetIds: DEFAULT_WIDGETS,
    layouts: generateDefaultLayout(DEFAULT_WIDGETS),
  };
}

export function useDashboardLayout() {
  const [state, setState] = useState<DashboardState>(loadInitialState);

  // Save to localStorage
  const save = useCallback((newState: DashboardState) => {
    setState(newState);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
  }, []);

  const onLayoutChange = useCallback(
    (newLayouts: Layout[]) => {
      save({ ...state, layouts: newLayouts });
    },
    [state, save]
  );

  const addWidget = useCallback(
    (widgetId: string) => {
      if (state.widgetIds.includes(widgetId)) return;
      const def = WIDGET_REGISTRY.find((w) => w.id === widgetId);
      if (!def) return;

      const { x, y } = findFreeSlot(state.layouts, def.defaultW, def.defaultH);
      const newLayouts: Layout[] = [
        ...state.layouts,
        { i: widgetId, x, y, w: def.defaultW, h: def.defaultH, minW: def.minW, minH: def.minH },
      ];
      save({ widgetIds: [...state.widgetIds, widgetId], layouts: newLayouts });
    },
    [state, save]
  );

  const removeWidget = useCallback(
    (widgetId: string) => {
      save({
        widgetIds: state.widgetIds.filter((id) => id !== widgetId),
        layouts: state.layouts.filter((l) => l.i !== widgetId),
      });
    },
    [state, save]
  );

  const resetLayout = useCallback(() => {
    save({
      widgetIds: DEFAULT_WIDGETS,
      layouts: generateDefaultLayout(DEFAULT_WIDGETS),
    });
  }, [save]);

  const widgets: WidgetDef[] = state.widgetIds
    .map((id) => WIDGET_REGISTRY.find((w) => w.id === id))
    .filter(Boolean) as WidgetDef[];

  return {
    widgets,
    layouts: state.layouts,
    widgetIds: state.widgetIds,
    onLayoutChange,
    addWidget,
    removeWidget,
    resetLayout,
    loaded: true,
  };
}

// Cache TTL helpers
export function getCacheTTL(): number {
  if (typeof window === "undefined") return 300;
  const val = localStorage.getItem(CACHE_TTL_KEY);
  return val ? parseInt(val, 10) : 300;
}

export function setCacheTTL(seconds: number) {
  localStorage.setItem(CACHE_TTL_KEY, String(seconds));
}
